export type ModelId = string

export interface ChatModelOption {
  id: ModelId
  label: string
}

/** usePersistentContext key for the user's configured default model (used when starting new chats) */
export const DEFAULT_MODEL_PERSISTENT_KEY = ["wingman-default-model"]

export const withModel = <TPayload extends object>(
  payload: TPayload,
  model: ModelId
): TPayload & { model: ModelId } => ({ ...payload, model })

export type PartType =
  | "text"
  | "thought"
  | "tool_call"
  | "tool_response"
  | "code_execution"
  | "code_result"
  | "agent_transfer"
  | "user_message"

export interface ChatPart {
  type: PartType
  content: string
  timestamp: string
  latency_ms: number
  metadata: any
  role: string
  index?: number
  author?: string
  session_id?: string
  message_id?: string
  part_id?: string
  entity_id?: string
  human_readable_name?: string
  chunk_mode?: "append" | "replace"
  seq?: number
  event_id?: string
  final?: boolean
}

export interface ChatMessage {
  id?: string
  role: "user" | "assistant" | "model"
  content: string
  parts?: ChatPart[]
  isComplete?: boolean
  attachments?: string[]
  status?: string
  timestamp?: string
}

// Backend severity values from phoenix WorkflowAnomaly.severity. Mirrors
// WastewaterCategory but normalized to upper-case and persisted on the
// chat row so the History sidebar can color rows without re-fetching the
// source anomaly.
export type WorkflowAnomalySeverity = "LOW" | "MEDIUM" | "HIGH" | "UNKNOWN"

export interface SessionInfo {
  id: string
  title: string
  created_at: string
  updated_at: string
  // Set when this chat was created from an anomaly investigation. Both
  // fields are null/undefined for regular (non-workflow) chats.
  workflow_anomaly_id?: string | null
  workflow_anomaly_severity?: WorkflowAnomalySeverity | null
  // Model used for this session (shorthand or raw model id)
  model?: ModelId | null
}

export interface ChatMessageResponse {
  id: string
  role: "user" | "assistant"
  content: string
  parts: ChatPart[]
  timestamp: string
  status: string
}
